import { Button, LoadingSpinner, Separator } from '@ais-ui/ui-core';
import { useMemo, useRef } from 'react';
import { useDashboardExport } from '../../../components/DetailsPage/useDashboardExport';
import { HistoricalAtonCard } from './historical/HistoricalAtonCard';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { useFetchAtonServices } from '@ais-ui/api';
import { useDetailedHostAton } from '../DataGrouping/useDetailedHostAton';
import { AtonMonitors } from './monitors/AtonMonitors';
import { useLocalization } from '@ais-ui/locale-management';

export const AtonDashboardLayout = () => {
  const dashboardRef = useRef<HTMLDivElement>(null);
  const { aton } = useSelectionState();
  const { messages } = useLocalization();

  const {
    data: atonServices,
    isLoading: isLoadingAtonServices,
    isError: isAtonServicesError,
  } = useFetchAtonServices(aton?.id);

  const atonName = useMemo(() => {
    return aton?.title || '';
  }, [aton]);

  const { handleDownload } = useDashboardExport(
    dashboardRef,
    `aton ${atonName} dashboard`
  );

  const {
    data: detailedAton,
    isLoading,
    isError,
  } = useDetailedHostAton(aton, atonServices);

  if (!aton) {
    return null;
  }

  const renderDashboardContent = () => {
    if (isLoading || isLoadingAtonServices) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <LoadingSpinner></LoadingSpinner>
          </div>
        </div>
      );
    }

    if (isError || isAtonServicesError) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Error Loading Dashboard
            </h3>
            <p className="text-muted-foreground mb-4">
              We encountered an error while loading the AtoN dashboard data.
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    // No data state
    if (!detailedAton) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-muted-foreground mb-4">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {messages.atonDashboardNoData}
            </h3>
            <p className="text-muted-foreground">
              No detailed information is available for this AtoN at the moment.
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        <AtonMonitors aton={detailedAton} />
        <HistoricalAtonCard aton={detailedAton} />
      </>
    );
  };

  return (
    <div
      className="h-full mr-4 space-y-4"
      id="dashboard-content"
      ref={dashboardRef}
    >
      <div className="flex items-end justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">{`AtoN ${atonName}`}</h2>
        <div className="flex items-center space-x-2 no-export">
          <Button
            onClick={handleDownload}
            className="no-export"
            disabled={
              isLoading ||
              isLoadingAtonServices ||
              isError ||
              isAtonServicesError ||
              !detailedAton
            }
          >
            Download
          </Button>
        </div>
      </div>
      <Separator />
      {renderDashboardContent()}
    </div>
  );
};
